import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { Icon } from './icon'

export type GuidedTourPlacement = 'top' | 'right' | 'bottom' | 'left' | 'center'

export interface GuidedTourStep {
  id: string
  target?: string | (() => HTMLElement | null)
  title: string
  description?: ReactNode
  placement?: GuidedTourPlacement
}

interface GuidedTourProps {
  open: boolean
  steps: GuidedTourStep[]
  current?: number
  defaultCurrent?: number
  storageKey?: string
  mask?: boolean
  onChange?: (index: number, step: GuidedTourStep) => void
  onClose?: () => void
  onFinish?: () => void
  onOpenChange?: (open: boolean) => void
  onSkip?: () => void
}

interface TourRect {
  top: number
  right: number
  bottom: number
  left: number
  width: number
  height: number
}

const HIGHLIGHT_PADDING = 8
const VIEWPORT_MARGIN = 12
const POPOVER_WIDTH = 340
const POPOVER_GAP = 14
const FALLBACK_VIEWPORT = { width: 1024, height: 768 }

function isGuidedTourCompleted(storageKey: string | undefined): boolean {
  if (!storageKey || typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(storageKey) === 'completed'
  } catch {
    return false
  }
}

function completeGuidedTour(storageKey: string | undefined): void {
  if (!storageKey || typeof window === 'undefined') return
  try {
    window.localStorage.setItem(storageKey, 'completed')
  } catch {
    // Ignore storage errors in private browsing modes.
  }
}

function clampIndex(index: number, steps: GuidedTourStep[]): number {
  if (steps.length === 0) return 0
  return Math.max(0, Math.min(index, steps.length - 1))
}

function resolveTarget(target: GuidedTourStep['target']): HTMLElement | null {
  if (typeof document === 'undefined' || !target) return null
  if (typeof target === 'function') return target()
  return document.querySelector<HTMLElement>(target)
}

function toTourRect(rect: DOMRect): TourRect {
  return {
    top: rect.top,
    right: rect.right,
    bottom: rect.bottom,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  }
}

function getViewportSize() {
  if (typeof window === 'undefined') return FALLBACK_VIEWPORT
  return { width: window.innerWidth, height: window.innerHeight }
}

function getHighlightRect(rect: TourRect): TourRect {
  const viewport = getViewportSize()
  const top = Math.max(VIEWPORT_MARGIN, rect.top - HIGHLIGHT_PADDING)
  const left = Math.max(VIEWPORT_MARGIN, rect.left - HIGHLIGHT_PADDING)
  const right = Math.min(viewport.width - VIEWPORT_MARGIN, rect.right + HIGHLIGHT_PADDING)
  const bottom = Math.min(viewport.height - VIEWPORT_MARGIN, rect.bottom + HIGHLIGHT_PADDING)

  return {
    top,
    left,
    right,
    bottom,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  }
}

function getPopoverStyle(
  rect: TourRect | null,
  placement: GuidedTourPlacement | undefined,
): CSSProperties {
  const viewport = getViewportSize()
  const width = Math.min(POPOVER_WIDTH, viewport.width - VIEWPORT_MARGIN * 2)
  const maxLeft = viewport.width - width - VIEWPORT_MARGIN
  const resolvedPlacement = rect ? (placement ?? 'bottom') : 'center'

  if (!rect || resolvedPlacement === 'center') {
    return {
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      width,
      maxWidth: 'calc(100vw - 24px)',
    }
  }

  let top = rect.bottom + POPOVER_GAP
  let left = rect.left + rect.width / 2 - width / 2

  if (resolvedPlacement === 'top') {
    top = Math.max(VIEWPORT_MARGIN, rect.top - POPOVER_GAP)
  }
  if (resolvedPlacement === 'left') {
    top = rect.top + rect.height / 2
    left = rect.left - width - POPOVER_GAP
  }
  if (resolvedPlacement === 'right') {
    top = rect.top + rect.height / 2
    left = rect.right + POPOVER_GAP
  }

  const transform =
    resolvedPlacement === 'top'
      ? 'translateY(-100%)'
      : resolvedPlacement === 'left' || resolvedPlacement === 'right'
        ? 'translateY(-50%)'
        : undefined

  return {
    top: Math.max(VIEWPORT_MARGIN, top),
    left: Math.max(VIEWPORT_MARGIN, Math.min(left, maxLeft)),
    width,
    maxWidth: 'calc(100vw - 24px)',
    transform,
  }
}

export function GuidedTour({
  open,
  steps,
  current,
  defaultCurrent = 0,
  storageKey,
  mask = true,
  onChange,
  onClose,
  onFinish,
  onOpenChange,
  onSkip,
}: GuidedTourProps) {
  const [internalCurrent, setInternalCurrent] = useState(() => clampIndex(defaultCurrent, steps))
  const [dismissed, setDismissed] = useState(() => isGuidedTourCompleted(storageKey))
  const [anchorRect, setAnchorRect] = useState<TourRect | null>(null)
  const activeIndex = clampIndex(current ?? internalCurrent, steps)
  const step = steps[activeIndex]
  const visible = open && !dismissed && steps.length > 0 && Boolean(step)
  const popoverStyle = useMemo(
    () => getPopoverStyle(anchorRect, step?.placement),
    [anchorRect, step?.placement],
  )

  const goTo = useCallback(
    (nextIndex: number) => {
      const next = clampIndex(nextIndex, steps)
      if (current === undefined) setInternalCurrent(next)
      onChange?.(next, steps[next])
    },
    [current, onChange, steps],
  )

  const close = useCallback(() => {
    completeGuidedTour(storageKey)
    setDismissed(true)
    onClose?.()
    onOpenChange?.(false)
  }, [onClose, onOpenChange, storageKey])

  const skip = useCallback(() => {
    completeGuidedTour(storageKey)
    setDismissed(true)
    onSkip?.()
    onOpenChange?.(false)
  }, [onOpenChange, onSkip, storageKey])

  const finish = useCallback(() => {
    completeGuidedTour(storageKey)
    setDismissed(true)
    onFinish?.()
    onOpenChange?.(false)
  }, [onFinish, onOpenChange, storageKey])

  useEffect(() => {
    if (!visible || !step) return

    let frame = 0
    const measure = () => {
      const target = resolveTarget(step.target)
      if (!target) {
        setAnchorRect(null)
        return
      }

      target.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' })
      const rect = target.getBoundingClientRect()
      setAnchorRect(toTourRect(rect))
    }
    const scheduleMeasure = () => {
      window.cancelAnimationFrame(frame)
      frame = window.requestAnimationFrame(measure)
    }

    scheduleMeasure()
    window.addEventListener('resize', scheduleMeasure)
    window.addEventListener('scroll', scheduleMeasure, true)

    return () => {
      window.cancelAnimationFrame(frame)
      window.removeEventListener('resize', scheduleMeasure)
      window.removeEventListener('scroll', scheduleMeasure, true)
    }
  }, [step, visible])

  useEffect(() => {
    if (!visible) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
      if (event.key === 'ArrowRight' && activeIndex < steps.length - 1) goTo(activeIndex + 1)
      if (event.key === 'ArrowLeft' && activeIndex > 0) goTo(activeIndex - 1)
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [activeIndex, close, goTo, steps.length, visible])

  if (!visible || !step || typeof document === 'undefined') return null

  const highlight = anchorRect ? getHighlightRect(anchorRect) : null
  const isLast = activeIndex === steps.length - 1

  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-[70]" aria-live="polite">
      {mask && <GuidedTourMask highlight={highlight} />}

      {highlight && (
        <div
          className="pointer-events-none fixed rounded-[18px] border-2 border-[#007aff] shadow-[0_0_0_4px_rgba(0,122,255,0.16),0_18px_48px_rgba(0,122,255,0.22)]"
          style={{
            top: highlight.top,
            left: highlight.left,
            width: highlight.width,
            height: highlight.height,
          }}
        />
      )}

      <section
        role="dialog"
        aria-modal="false"
        aria-label={step.title}
        className="glass-panel pointer-events-auto fixed overflow-hidden rounded-2xl border px-4 py-4 text-left shadow-[0_28px_90px_rgba(17,24,39,0.20)] backdrop-blur-2xl"
        style={popoverStyle}
      >
        <div className="flex items-start gap-3">
          <div className="apple-primary flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
            <Icon name="auto_awesome_mosaic" className="text-sm" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-on-surface text-sm leading-5 font-semibold">{step.title}</p>
            {step.description && (
              <div className="text-on-surface-variant mt-1 text-xs leading-5">
                {step.description}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="关闭引导"
            className="text-on-surface-variant hover:text-on-surface focus-visible:ring-primary/25 flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-black/[0.04] focus-visible:ring-2 focus-visible:outline-none"
          >
            <Icon name="close" className="text-sm" />
          </button>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <span className="text-on-surface-variant mr-auto text-xs">
            {activeIndex + 1}/{steps.length}
          </span>
          <button
            type="button"
            onClick={skip}
            aria-label="跳过引导"
            className="text-on-surface-variant hover:text-on-surface rounded-full px-3 py-1.5 text-xs transition-colors hover:bg-black/[0.04]"
          >
            跳过引导
          </button>
          <button
            type="button"
            onClick={() => goTo(activeIndex - 1)}
            disabled={activeIndex === 0}
            className="glass-field text-on-surface rounded-full px-3 py-1.5 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40"
          >
            上一步
          </button>
          <button
            type="button"
            onClick={() => (isLast ? finish() : goTo(activeIndex + 1))}
            className="apple-primary rounded-full px-3.5 py-1.5 text-xs font-medium"
          >
            {isLast ? '完成' : '下一步'}
          </button>
        </div>
      </section>
    </div>,
    document.body,
  )
}

function GuidedTourMask({ highlight }: { highlight: TourRect | null }) {
  if (!highlight) {
    return <div className="pointer-events-auto fixed inset-0 bg-black/45 backdrop-blur-[2px]" />
  }

  const viewport = getViewportSize()
  const rightWidth = Math.max(0, viewport.width - highlight.right)
  const bottomHeight = Math.max(0, viewport.height - highlight.bottom)

  return (
    <>
      <div
        className="pointer-events-auto fixed bg-black/45 backdrop-blur-[2px]"
        style={{ top: 0, left: 0, right: 0, height: highlight.top }}
      />
      <div
        className="pointer-events-auto fixed bg-black/45 backdrop-blur-[2px]"
        style={{ top: highlight.bottom, left: 0, right: 0, height: bottomHeight }}
      />
      <div
        className="pointer-events-auto fixed bg-black/45 backdrop-blur-[2px]"
        style={{
          top: highlight.top,
          left: 0,
          width: highlight.left,
          height: highlight.height,
        }}
      />
      <div
        className="pointer-events-auto fixed bg-black/45 backdrop-blur-[2px]"
        style={{
          top: highlight.top,
          right: 0,
          width: rightWidth,
          height: highlight.height,
        }}
      />
    </>
  )
}
